import React, { PureComponent } from 'react';
import { withStyles } from '@material-ui/styles';
import { Typography } from '@material-ui/core';
import Select, { components, Async } from 'react-select';
import PropTypes from 'prop-types';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import cx from 'classnames';
import { REPLY_STATUS_COLORS } from 'constants/outreach';
import { isEmpty } from 'lodash';
import PlusCircleOutlineIcon from 'mdi-react/PlusCircleOutlineIcon';
import Button from 'components/buttons/Button';

import {
  primary1000,
  secondary1000,
  secondary300,
  secondary50,
  disabledText,
  transparent,
} from '../../../src/themes/colors';

const styles = theme => ({
  placeholder: {
    fontSize: '0.875rem',
    color: `${disabledText} !important`,
    fontWeight: '400 !important',
    fontFamily: theme.typography.fontFamily,
    position: 'absolute',
  },
  multiPlaceholder: {
    transform: 'none !important',
    position: 'static !important',
  },
  indicatorSeparator: {
    display: 'none',
  },
  valueContainer: {
    order: 0,
    flex: 1,
    fontSize: '0.875rem',
    width: '100%',
  },
  indicatorsContainer: {
    flex: 0,
  },
  closeIcon: {
    color: theme.palette.secondary['0'],
    width: '0.875rem',
    cursor: 'pointer',
  },
  menu: {
    border: 'none !important',
    boxShadow: `${theme.shadows[2]} !important`,
    borderRadius: '0.375rem !important',
    paddingBottom: '0.5rem',
    paddingTop: '0.5rem',
    zIndex: '2 !important',
  },
});

const ValueContainer = ({ children, ...props }) => (
  <div className={props.selectProps.classes.valueContainer} >
    <components.ValueContainer {...props}>
      {children}
      {props.selectProps.isMulti && !isEmpty(props.selectProps.value) &&
        <components.Placeholder
          {...props}
          isFocused={props.isFocused}
          className={`
            ${props.selectProps.classes.placeholder}
            ${props.selectProps.classes.multiPlaceholder}
          `}
        >
          {props.selectProps.multiPlaceholder}
        </components.Placeholder>
      }
    </components.ValueContainer>
  </div>
);

const MultiValueRemove = props => (
  <components.MultiValueRemove {...props}>
    <CloseRoundedIcon className={props.selectProps.classes.closeIcon} />
  </components.MultiValueRemove>
);
const Menu = (props) => {
  const {
    classes,
    creatable,
    disableCreate,
    inputValue,
    handleCreate,
  } = props.selectProps;
  return (
    <components.Menu
      {...props}
      className={cx(classes.menu, props.className)}
    >
      {props.children}
      {creatable &&
        <Button
          variant="text"
          className="mt-2"
          disabled={disableCreate && !inputValue}
          startIcon={<PlusCircleOutlineIcon />}
          onClick={() => handleCreate(inputValue)}
        >
          Create New
        </Button>
      }
    </components.Menu>
  );
};
const IndicatorSeparator = props => (
  // eslint-disable-next-line react/prop-types
  <span className={props.selectProps.classes.indicatorSeparator} />
);
const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <KeyboardArrowDownRoundedIcon />
  </components.DropdownIndicator>
);
const IndicatorsContainer = props => (
  <div className={props.selectProps.classes.indicatorsContainer} >
    <components.IndicatorsContainer {...props} />
  </div>
);
const Input = props => (
  <components.Input {...props} className={props.selectProps.classes.input} />
);
const Placeholder = props => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.placeholder}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);
const Control = props => (
  <components.Control {...props} className={props.selectProps.classes.control} />
);
const selectComponents = {
  MultiValueRemove,
  DropdownIndicator,
  IndicatorsContainer,
  ValueContainer,
  IndicatorSeparator,
  Input,
  Placeholder,
  Control,
  Menu,
};
class MultiSelect extends PureComponent {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    loadOptions: PropTypes.func,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    multiPlaceholder: PropTypes.string,
    handleRemoveSelection: PropTypes.func,
    classes: PropTypes.shape({}).isRequired,
    theme: PropTypes.shape({}).isRequired,
    options: PropTypes.shape({}),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({}),
        ]),
        label: PropTypes.string,
      })),
    ]).isRequired,
    isMulti: PropTypes.bool,
    isClearable: PropTypes.bool,
    className: PropTypes.string,
    label: PropTypes.string,
    defaultValue: PropTypes.shape({}),
    isDisabled: PropTypes.bool,
    onBlur: PropTypes.func,
    handleScrollToBottom: PropTypes.func,
    hideSelectedOptions: PropTypes.bool,
    isLoading: PropTypes.bool,
    isCategorySelect: PropTypes.bool,
    noBorder: PropTypes.bool,
    size: PropTypes.string,
    optionsRef: PropTypes.shape,
    onInputChange: PropTypes.func,
    creatable: PropTypes.bool,
    disableCreate: PropTypes.bool,
    handleCreate: PropTypes.func,
    displayProperty: PropTypes.string,
  };

  static defaultProps = {
    className: null,
    label: null,
    placeholder: '',
    multiPlaceholder: 'Set value',
    value: null,
    options: [],
    handleRemoveSelection: null,
    isMulti: false,
    isClearable: false,
    loadOptions: null,
    defaultValue: null,
    isDisabled: false,
    onBlur: () => { },
    handleScrollToBottom: () => { },
    hideSelectedOptions: true,
    isLoading: false,
    isCategorySelect: false,
    noBorder: false,
    size: 'medium',
    optionsRef: null,
    creatable: false,
    disableCreate: false,
    handleCreate: null,
    onInputChange: () => { },
    displayProperty: 'flex',
  };

  state = {
    input: '',
  }

  handleChange = (value) => {
    this.props.onChange(value);
  };

  handleRemoveSelection = (selectedValue) => {
    this.props.handleRemoveSelection(selectedValue);
  };

  handleInputChange = (input) => {
    if (input !== this.state.input) {
      this.props.onInputChange(input);
      this.setState({ input });
    }
  };

  render() {
    const {
      className,
      label,
      value,
      name,
      options,
      defaultValue,
      isMulti,
      isClearable,
      placeholder,
      multiPlaceholder,
      loadOptions,
      classes,
      isDisabled,
      onBlur,
      hideSelectedOptions,
      isLoading,
      theme,
      size,
      isCategorySelect,
      noBorder,
      optionsRef,
      creatable,
      disableCreate,
      handleCreate,
      displayProperty,
      handleScrollToBottom,
    } = this.props;
    const selectStyles = {
      control: base => ({
        ...base,
        minHeight: size === 'small' ? '2.5rem' : '3rem',
        border: `0.063rem solid ${noBorder ? transparent : secondary300}`,
        borderRadius: '0.375rem',
        boxShadow: 'none',
        '&:hover, &:focus, &:focus-within, &:active': {
          border: `0.063rem solid ${noBorder ? transparent : primary1000}`,
          boxShadow: 'none',
        },
      }),
      input: base => ({
        ...base,
        color: secondary1000,
        margin: 0,
        '& input': {
          font: 'inherit',
        },
      }),
      multiValueContainer: base => ({
        ...base,
        padding: '0.625rem 0.75rem',
      }),
      multiValue: (base, state) => {
        const color = isCategorySelect ?
          theme.palette[REPLY_STATUS_COLORS[state.data.value]] :
          primary1000;
        return {
          ...base,
          boxShadow: 'none',
          backgroundColor: color,
          height: '1.5rem',
          borderRadius: '0.375rem',
          display: displayProperty,
        };
      },
      multiValueLabel: base => ({
        ...base,
        color: theme.palette.secondary['0'],
        fontSize: '0.75rem',
        fontWeight: '600',
        paddingTop: '0.125rem',
        paddingLeft: '0.5rem',
      }),
      multiValueRemove: base => ({
        ...base,
        '&:hover': {
          backgroundColor: transparent,
          color: theme.palette.secondary['0'],
        },
      }),
      indicatorsContainer: base => ({
        ...base,
        display: noBorder ? 'none' : 'flex',
      }),
      option: base => ({
        ...base,
        minHeight: '2.5rem',
        color: theme.palette.secondary.text,
        backgroundColor: theme.palette.secondary['0'],
        fontSize: '0.875rem',
        '&:hover': {
          backgroundColor: secondary50,
        },
      }),
      singleValue: base => ({
        ...base,
        color: theme.palette.secondary.text,
        marginLeft: '0.25rem',
      }),
      dropdownIndicator: (base, state) => ({
        ...base,
        transition: 'all 0.3s',
        color: state.isFocused ? theme.palette.primary.main : theme.palette.secondary['300'],
        padding: size === 'small' ? '0.25rem' : undefined,
        transform: state.isFocused ? 'rotate(180deg)' : 'none',
        '&:hover, &:focus, &:focus-within, &:active': {
          color: theme.palette.primary.main,
        },
      }),
    };

    if (loadOptions) {
      return (
        <React.Fragment>
          {label &&
            /* eslint-disable jsx-a11y/label-has-for */
            <label>{label}</label>
          }
          <div className={className}>
            <Async
              classes={classes}
              ref={optionsRef}
              name={name}
              value={value}
              defaultValue={defaultValue}
              styles={selectStyles}
              onChange={this.handleChange}
              components={selectComponents}
              placeholder={placeholder}
              multiPlaceholder={multiPlaceholder}
              loadOptions={loadOptions}
              options={options}
              isMulti={isMulti}
              isClearable={isClearable}
              defaultOptions
              textFieldProps={{
                InputLabelProps: {
                  shrink: true,
                },
              }}
              isDisabled={isDisabled}
              onBlur={onBlur}
              hideSelectedOptions={hideSelectedOptions}
              isLoading={isLoading}
              isCategorySelect={isCategorySelect}
              creatable={creatable}
              disableCreate={disableCreate}
              handleCreate={handleCreate}
            />
          </div>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        {label &&
          /* eslint-disable jsx-a11y/label-has-for */
          <label>{label}</label>
        }
        <div className={className}>
          <Select
            classes={classes}
            className={className}
            name={name}
            value={value}
            defaultValue={defaultValue}
            styles={selectStyles}
            onChange={this.handleChange}
            components={selectComponents}
            placeholder={placeholder}
            multiPlaceholder={multiPlaceholder}
            options={options}
            isMulti={isMulti}
            isClearable={isClearable}
            textFieldProps={{
              InputLabelProps: {
                shrink: true,
              },
            }}
            isDisabled={isDisabled}
            onBlur={onBlur}
            hideSelectedOptions={hideSelectedOptions}
            isLoading={isLoading}
            isCategorySelect={isCategorySelect}
            onInputChange={this.handleInputChange}
            creatable={creatable}
            disableCreate={disableCreate}
            handleCreate={handleCreate}
            onMenuScrollToBottom={handleScrollToBottom}
          />
        </div>
      </React.Fragment>
    );
  }
}
export default withStyles(styles, { withTheme: true })(MultiSelect);
